import IosShareIcon from '@mui/icons-material/IosShare';
import SearchIcon from "@mui/icons-material/Search";
import {
  Button,
  CircularProgress,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import { getOrInitTwilioClient } from 'app/DesktopLayout/twilioClient';
import { awsImageRootUrl, baseWebUrl } from "lib/constants";
import { Availability } from "lib/features/availability/types";
import { useLazyGetBusinessesQuery } from "lib/features/business/businessService";
import { useGetAllInfluencersQuery } from "lib/features/influencer/influencerService";
import { FareharborAvailability, Item } from "lib/features/item/types";
import { CreateReferralDto } from "lib/features/referral/referralService";
import { User } from "lib/features/user/types";
import { selectUser } from "lib/features/user/userSlice";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import authServices from "services/auth-service";
import businessServices from 'services/business-service';
import chatServices from "services/chat-service";
import referralServices from "services/referral-service";
import { availabilityModalStyles } from "styles/AvailabilityDirectory/availabilityModal.styles";
import CrossIcon from "../../../assets/icons/cross.svg";


export interface ShareAvailabilityModalProps {
  open: boolean;
  close: () => void;
  customStyle?: object;
  sx?: object;
  selectedAvailability: Availability | FareharborAvailability;
  item: Item;
}

export type UserOrBusiness = User & {
  business_name?: string;
  business_headline?: string;
  business_id?: number;
  businessImage?: string;
};

export default function ShareAvailabilityModal({
  open,
  close,
  customStyle = {},
  sx = {},
  selectedAvailability,
  item,
}: ShareAvailabilityModalProps) {
  const user = useSelector(selectUser);
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState("");
  const [userData, setUserData] = useState<User>();
  const [loading, setLoading] = useState<boolean>(false);
  const [combinedData, setCombinedData] = useState<UserOrBusiness[]>([]);
  const [handleConfirmDialog, setHandleConfirmDialog] = useState<boolean>(false)
  const [filteredInfluencerData, setFilteredInfluencerData] =
    useState<UserOrBusiness[]>([]);
  const [selectedInfluencer, setSelectedInfluencer] = useState<number>();
  const [selectedInfluencerDetails, setSelectedInfluencerDetails] = useState<UserOrBusiness>();
  const [currentShowTab, setCurrentShowTab] = useState<string>("referees");
  const [myBusinessImage, setMyBusinessImage] = useState<string | null>(null);
  console.log("selected availability", selectedAvailability)
  const handleShare = async () => {
    if (!user?.id || !selectedInfluencer || !selectedAvailability) {
      return; // prevents sending an invalid payload
    }

    try {
      setLoading(true);

      let referralPayload: CreateReferralDto;

      // Internal availability (non-FareHarbor)
      if (item.item_type !== 4) {
        const internalAvailability = selectedAvailability as Availability;

        referralPayload = {
          user_id: user.id,
          business_id: item.business.id,
          availability_id: internalAvailability.id,
          item_id: item.id,
          referral_type: 1,
          state: 1,
        };
      } else {
        // FareHarbor availability
        const fhAvailability = selectedAvailability as FareharborAvailability;

        referralPayload = {
          user_id: user.id,
          business_id: item.business.id,
          referral_type: 4,
          external_availability_id: fhAvailability.pk,
          fareharbor_customer_prototype_id:
            fhAvailability.customer_type_rates?.[0]?.pk,
          item_id: item.id,
          state: 1,
        };
      }

      const referralResponse = await referralServices.createReferral(referralPayload);
      const referralId = referralResponse?.data?.id;

      if (!referralId) {
        throw new Error("Missing referral id from createReferral response");
      }

      const url = `${baseWebUrl}/checkout/${referralId}`;

      // ✅ always send a defined participants array
      const payload = {
        participants: [String(user.id), String(selectedInfluencer)],
        initialMessage: {
          authorId: String(user.id),
          content: url,
        },
      };

      const response = await chatServices.createChannel(payload);
      const sid = response?.data?.conversation as string;
      const fetchToken = async () => {
        const res = await chatServices.getUserTokenForChat(String(user.id));
        return String(res.data.token);
      };

      const client = await getOrInitTwilioClient(fetchToken);
      const conversation = await client.getConversationBySid(sid);

      // Send the message directly via Twilio SDK
      await conversation.sendMessage(url);
      if (sid) {
        const name = selectedInfluencerDetails?.business_name || selectedInfluencerDetails?.first_name + " " + selectedInfluencerDetails?.last_name;
        const avatar = selectedInfluencerDetails?.businessImage || selectedInfluencerDetails?.avatar;
        const myavatar = myBusinessImage || user?.avatar;
        navigate(`/chat/${sid}/${avatar}/${name}/${myavatar}`);
      }

      close();
    } catch (error) {
      console.error("Error creating/opening conversation:", error);
    } finally {
      setLoading(false);
    }
  };
  const regionId = userData?.regions?.[0]?.region?.id;

  const { data: influencerData, isLoading: isLoadingInfluencers } =
    useGetAllInfluencersQuery(regionId as number, { skip: !regionId });

  const [getBusinesses, { data: businessData, isLoading: isLoadingBusinesses }] =
    useLazyGetBusinessesQuery();

  useEffect(() => {
    const fetchUserDetails = async () => {
      if (user?.id) {
        try {
          const response = await authServices.fetchUser(user.id);
          setUserData(response?.data);
        } catch (error: any) {
          console.error("Error fetching chats:", error?.message);
        }
      }
    };

    const fetchMyBusinessImage = async () => {
      if (user?.id) {
        try {
          const response = await businessServices.getUserBusiness(user.id);
          const businessImage = response?.data?.[0]?.businessImages?.[0]?.uri;
          if (businessImage) {
            setMyBusinessImage(businessImage);
          }
        } catch (error: any) {
          console.error("Error fetching user business:", error?.message);
        }
      }
    };

    fetchUserDetails();
    fetchMyBusinessImage();
  }, [user?.id]);

  // Fetch businesses
  useEffect(() => {
    getBusinesses(null);
  }, [getBusinesses]);

  // Combine influencers and businesses
  useEffect(() => {
    const combined: UserOrBusiness[] = [];

    if (influencerData) {
      combined.push(...influencerData);
    }

    if (businessData) {
      const businessUsers = businessData
        .filter((business: any) => business.business_users && business.business_users.length > 0)
        .map((business: any) => {
          const user = business.business_users[0];
          return {
            ...user,
            business_name: business.business,
            business_headline: business.business_headline,
            business_id: business.id,
            businessImage: business.businessImages && business.businessImages.length > 0 ? business.businessImages[0].uri : null,
          };
        });
      combined.push(...businessUsers);
    }

    setCombinedData(combined);
  }, [influencerData, businessData]);

  // Filter combined data based on tab and search
  useEffect(() => {
    let filtered = combinedData || [];

    // Filter by tab
    if (currentShowTab === "referees") {
      filtered = filtered.filter((item: UserOrBusiness) => !item?.business_name);
    } else if (currentShowTab === "businesses") {
      filtered = filtered.filter((item: UserOrBusiness) => !!item?.business_name);
    }

    // Filter by search
    if (searchValue.trim() !== "") {
      filtered = filtered.filter((item: UserOrBusiness) => {
        const fullName = `${item?.first_name?.toLowerCase() || ''} ${item?.last_name?.toLowerCase() || ''}`;
        const businessName = item?.business_name?.toLowerCase() || '';
        const searchTerm = searchValue.toLowerCase();
        return fullName.includes(searchTerm) || businessName.includes(searchTerm);
      });
    }

    setFilteredInfluencerData(filtered);
  }, [searchValue, combinedData, currentShowTab]);
  return (
    <>
      <Modal
        open={open}
        onClose={close}
        aria-labelledby="customer-modal-title"
        aria-describedby="customer-modal-description"
        sx={{ ...sx }}
      >
        <Box sx={{ ...availabilityModalStyles.modalShareStyles, ...customStyle, display: "flex", flexDirection: "column", alignItems: "center" }}>
          <Box sx={availabilityModalStyles.editHeader}>
            {/* Modal Title */}
            <Typography sx={availabilityModalStyles.headerText}>
              Share Availability
            </Typography>

            {/* Cross */}
            <img
              src={CrossIcon}
              style={availabilityModalStyles.closeIcon}
              onClick={close}
            />
          </Box>
          <Box sx={{ display: "flex", justifyContent: "center", gap: "16px", width: "264px", height: "37px", mx: "auto", mb: "16px" }}>
            <Box sx={{ cursor: "pointer", borderBottom: currentShowTab === "referees" ? "3px solid #328734" : "none" }} onClick={() => setCurrentShowTab("referees")}>
              <Typography variant="h2" sx={{ fontFamily: "Poppins", fontWeight: "700", fontSize: "24px", lineHeight: "34px", color: currentShowTab === "referees" ? "#000" : "#8D8D8D" }}>
                Referees
              </Typography>
            </Box>
            <Box sx={{ cursor: "pointer", borderBottom: currentShowTab === "businesses" ? "3px solid #328734" : "none" }} onClick={() => setCurrentShowTab("businesses")}>
              <Typography variant="h2" sx={{ fontFamily: "Poppins", fontWeight: "700", fontSize: "24px", lineHeight: "34px", color: currentShowTab === "businesses" ? "#000" : "#8D8D8D" }}>
                Businesses
              </Typography>
            </Box>
          </Box>

          <Box sx={availabilityModalStyles.searchContainer}>
            <TextField
              variant="outlined"
              placeholder="Search"
              fullWidth
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              sx={availabilityModalStyles.searchInput}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "gray" }} />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Modal Body */}
          {/* <Box mt={2} display="grid" gridColumn={3} sx={availabilityModalStyles.modalBody}>
          {(isLoadingInfluencers || isLoadingBusinesses) ? (
            <Typography sx={{ textAlign: "center", padding: "20px" }}>
              Loading...
            </Typography>
          ) : (
            <>
              {filteredInfluencerData?.map((item: UserOrBusiness) => (
                <Box
                  key={item?.id}
                  sx={[
                    availabilityModalStyles.cardBody,
                    {
                      backgroundColor:
                        selectedInfluencer == item?.id ? "#D0EED5" : "#fff",
                    },
                  ]}
                  onClick={() => {
                    setSelectedInfluencer(item?.id);
                  }}
                >
                  <Box sx={{ marginRight: "16px" }}>
                    <img
                      src={`${awsImageRootUrl}/${item?.avatar}`}
                      style={{
                        ...availabilityModalStyles.influencerImage,
                        objectFit: "cover" as "cover",
                      }}
                    />
                  </Box>

                  <Box sx={availabilityModalStyles.cardBodyContainer}>
                    <Typography sx={availabilityModalStyles.name}>
                      {item?.business_name
                        ? item.business_name
                        : `${item?.first_name} ${item?.last_name?.slice(0, 1)}.`
                      }
                    </Typography>

                    <Typography sx={availabilityModalStyles.company}>
                      {item?.business_headline || item?.influencer_type?.type}
                    </Typography>
                    <Typography sx={availabilityModalStyles.location}>
                      {item?.regions?.[0]?.region?.region}
                    </Typography>
                  </Box>
                </Box>
              ))}
              {filteredInfluencerData?.length === 0 && (
                <Typography sx={{ textAlign: "center", padding: "20px" }}>
                  No Record
                </Typography>
              )}
            </>
          )}
        </Box> */}

          <Box
            mt={2}
            sx={{
              display: "flex",
              flexWrap: "wrap",
              width: "978px"
            }}
          >
            {(isLoadingInfluencers || isLoadingBusinesses) ? (
              <Typography sx={{ textAlign: "center", py: 2 }}>Loading...</Typography>
            ) : (
              <>
                {filteredInfluencerData?.map((item: UserOrBusiness) => (
                  <Box
                    key={item?.id}
                    onClick={() => { setSelectedInfluencer(item?.id); setSelectedInfluencerDetails(item); setHandleConfirmDialog(true) }}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                      width: "314px",
                      height: "104px",
                      gap: "12px",
                      ml: "8px",
                      pl: "4px",
                      backgroundColor:
                        selectedInfluencer === item?.id ? "#D0EED5" : "#fff",
                      transition: "all 0.2s ease",
                      "&:hover": { backgroundColor: "#D0EED5" },
                    }}
                  >
                    <Box sx={{ flexShrink: 0, mr: "12px" }}>
                      {
                        item?.businessImage ?
                          <Box
                            component="img"
                            src={`${awsImageRootUrl}/${item?.businessImage}`}
                            alt={item?.first_name}
                            sx={{
                              width: 80,
                              height: 80,
                              borderRadius: "8px",
                              objectFit: "cover",
                            }}
                          /> :
                          <Box
                            component="img"
                            src={`${awsImageRootUrl}/${item?.avatar}`}
                            alt={item?.first_name}
                            sx={{
                              width: 80,
                              height: 80,
                              borderRadius: "8px",
                              objectFit: "cover",
                            }}
                          />
                      }
                    </Box>

                    <Box sx={{ flex: 1, overflow: "hidden", width: "222px", height: "92px", maxHeight: "92px", padding: "10px 16px 10px 0", borderBottom: "0.5px solid #8D8D8D", boxSizing: "border-box" }}>
                      <Typography
                        variant="subtitle1"
                        sx={{ fontWeight: 700, color: "#000", fontSize: "17px", textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}
                      >
                        {item?.business_name
                          ? item.business_name
                          : `${item?.first_name} ${item?.last_name?.slice(0, 1)}.`}
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary", fontWeight: 400, textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap", fontSize: "15px" }}
                      >
                        {item?.business_headline || item?.influencer_type?.type}
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary", fontSize: "15px", fontWeight: 400 }}
                      >
                        {item?.regions?.[0]?.region?.region}
                      </Typography>
                    </Box>
                  </Box>
                ))}

                {filteredInfluencerData?.length === 0 && (
                  <Typography sx={{ textAlign: "center", py: 2 }}>
                    No Record
                  </Typography>
                )}
              </>
            )}
          </Box>
        </Box>
      </Modal>
      <Modal
        open={handleConfirmDialog}
        onClose={() => setHandleConfirmDialog(false)}
        aria-labelledby="customer-modal-title"
        aria-describedby="customer-modal-description"
        sx={{ ...sx }}
      >
        <Box sx={{ ...availabilityModalStyles.modalShareStyles, p: "24px 22px", display: "flex", flexDirection: "column", alignItems: "center", gap: "15px", width: "495px", overflow: "hidden" }}>
          <Box sx={{ ...availabilityModalStyles.editHeader, height: "30px" }}>
            {/* Modal Title */}
            <Typography sx={{ ...availabilityModalStyles.headerText, fontSize: "22px", fontWeight: 600, lineHeight: "30px", fontFamily: "Poppins" }}>
              Share Availability
            </Typography>
          </Box>

          <Box >
            <Typography sx={{ textAlign: "center", fontSize: "17px", fontWeight: 400, lineHeight: "25.5px" }} >
              Are you sure you want to share this availability with <span style={{ fontWeight: "600" }}>{selectedInfluencerDetails?.business_name || selectedInfluencerDetails?.first_name + " " + selectedInfluencerDetails?.last_name}</span>?
            </Typography>
          </Box>

          {/* Modal Footer */}
          <Box sx={{ ...availabilityModalStyles.shareModalFooter, height: "60px", alignItems: "center", mt: 0 }}>
            <Button
              sx={{ ...availabilityModalStyles.greenEditButton, borderRadius: "8px", width: "150px", fontWeight: 600, height: "44px", p: "8px 24px" }}
              onClick={handleShare}
              startIcon={<IosShareIcon />}
            >
              {loading ? (
                <CircularProgress
                  sx={availabilityModalStyles.circularProgress}
                  size={20}
                />
              ) : (
                "Share"
              )}
            </Button>
            <Button
              sx={{ ...availabilityModalStyles.whiteButton, borderRadius: "8px", width: "150px", fontWeight: 600, height: "44px", p: "8px 24px" }}
              disabled={loading}
              onClick={() => { !loading && setHandleConfirmDialog(false) }}
            >
              Cancel
            </Button>
          </Box>
        </Box>
      </Modal>
    </>
  );
}